import { DATABASE_TYPES } from './interfaces/IDatabaseAdapter.js'
// FirestoreAdapter removed - MongoDB only

/**
 * Database Service Factory
 * Creates and manages database adapter instances
 */
class DatabaseService {
  adapter
  config
  initialized

  constructor() {
    this.adapter = null
    this.config = null
    this.initialized = false
  }

  /**
   * Initialize the database service with the specified adapter
   * @param {string} type - Database type (from DATABASE_TYPES)
   * @param {Object} config - Database configuration
   */
  async initialize(type, config) {
    try {
      // Create the appropriate adapter
      this.adapter = await this._createAdapter(type)
      this.config = config
      
      // Initialize the adapter
      await this.adapter.initialize(config)
      this.initialized = true
      
    } catch (error) {
      console.error('Failed to initialize database service:', error)
      throw error
    }
  }

  /**
   * Create adapter instance based on type
   * @param {string} type - Database type
   * @returns {IDatabaseAdapter} Database adapter instance
   */
  async _createAdapter(type) {
    switch (type) {
      case DATABASE_TYPES.HTTP:
        // Direct HTTP adapter usage
        try {
          const { HttpAdapter } = await import('./adapters/HttpAdapter.js')
          const httpAdapter = new HttpAdapter()
          return httpAdapter
        } catch (error) {
          throw new Error(`Failed to create HttpAdapter: ${error.message}`)
        }
      
      case DATABASE_TYPES.MONGODB:
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
          // Browser environment - FORCE HttpAdapter usage
          
          // Try API server first for real-time sync
          try {
            const healthResponse = await fetch('https://dev.services.leadconnectorhq.com/hltestmanager/health')
            if (healthResponse.ok) {
              const { HttpAdapter } = await import('./adapters/HttpAdapter.js')
              return new HttpAdapter()
            } else {
              throw new Error('API server not responding')
            }
          } catch (error) {
            console.warn('API server not available, using MockAdapter:', error.message)
            const { MockAdapter } = await import('./adapters/MockAdapter.js')
            return new MockAdapter()
          }
        } else {
          // Node.js environment - use real MongoDB adapter
          try {
            const { MongoDBAdapter } = await import('./adapters/MongoDBAdapter.js')
            return new MongoDBAdapter()
          } catch (error) {
            throw new Error('MongoDB adapter requires "mongodb" package. Install with: npm install mongodb')
          }
        }
      
      default:
        throw new Error(`Unsupported database type: ${type}`)
    }
  }

  /**
   * Ensure the service is initialized
   */
  _ensureInitialized() {
    if (!this.initialized) {
      throw new Error('Database service not initialized. Call initialize() first.')
    }
  }

  // Proxy all adapter methods
  async get(collection, id) {
    this._ensureInitialized()
    return this.adapter.get(collection, id)
  }

  async list(collection, options = {}) {
    this._ensureInitialized()
    return this.adapter.list(collection, options)
  }

  async query(collection, conditions, options = {}) {
    this._ensureInitialized()
    return this.adapter.query(collection, conditions, options)
  }

  async create(collection, data, id = null) {
    this._ensureInitialized()
    return this.adapter.create(collection, data, id)
  }

  async update(collection, id, data, merge = true) {
    this._ensureInitialized()
    return this.adapter.update(collection, id, data, merge)
  }

  async upsert(collection, id, data) {
    this._ensureInitialized()
    return this.adapter.upsert(collection, id, data)
  }

  async delete(collection, id) {
    this._ensureInitialized()
    return this.adapter.delete(collection, id)
  }

  async batch(operations) {
    this._ensureInitialized()
    return this.adapter.batch(operations)
  }

  onSnapshot(collection, options, callback) {
    this._ensureInitialized()
    return this.adapter.onSnapshot(collection, options, callback)
  }

  async searchDocuments(collection, searchTerm, options = {}) {
    this._ensureInitialized()
    return this.adapter.searchDocuments(collection, searchTerm, options)
  }

  /**
   * Ensure indexes exist for a collection
   * @param {string} collection - Collection name
   * @param {Array} indexes - Array of index definitions
   */
  async ensureIndexes(collection, indexes) {
    if (!this.initialized || !this.adapter) {
      throw new Error('Database service not initialized')
    }

    // For HttpAdapter, we'll skip index creation as it's handled by the API server
    if (this.adapter.constructor.name === 'HttpAdapter') {
      return
    }

    // For MongoDBAdapter, create indexes
    if (this.adapter.constructor.name === 'MongoDBAdapter') {
      try {
        for (const indexDef of indexes) {
          await this.adapter.ensureIndex(collection, indexDef)
        }
      } catch (error) {
        console.warn(`⚠️ Could not ensure indexes for ${collection}:`, error.message)
      }
    }
  }

  async close() {
    if (this.adapter) {
      await this.adapter.close()
      this.adapter = null
      this.initialized = false
    }
  }

  /**
   * Get the current adapter type
   */
  getAdapterType() {
    if (!this.initialized) return null
    return this.adapter.constructor.name
  }

  /**
   * Check if service is initialized
   */
  isInitialized() {
    return this.initialized
  }

  /**
   * Set authentication token for HTTP adapter
   */
  setAuthToken(token) {
    if (this.adapter && typeof this.adapter.setAuthToken === 'function') {
      this.adapter.setAuthToken(token)
    }
  }

  /**
   * Clear authentication token
   */
  clearAuthToken() {
    if (this.adapter && typeof this.adapter.clearAuthToken === 'function') {
      this.adapter.clearAuthToken()
    }
  }
}

// Create singleton instance
const databaseService = new DatabaseService()

export default databaseService
export { DatabaseService, DATABASE_TYPES }
